import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { PolyglotBehavior, pluralize } from '../fit-polyglot/fit-polyglot-behavior';

import styles from './fit-rating.css';

/**
 * Display rating with stars, value (optional), text (optional) and count (optional).
 *
 * \n<code>\n   &lt;fit-rating value=\"3.7\"&gt;&lt;/fit-rating&gt;\n</code>"
 *
 * @element fit-rating
 */
class FitRating extends PolyglotBehavior(LitElement) {
  static get is() { return 'fit-rating'; }

  static get styles() { return styles; }

  render() {
    return html`
      <div class="rating-text" ?hidden="${this.text == ''}">${this.text}</div>
      <div class="rating-line">
        <figure class="rating-scale" id="scale">
          <svg class="rating-mask" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="80px" height="16px" viewBox="0 0 80 16" enable-background="new 0 0 80 16" xml:space="preserve">
            <defs>
            <clipPath id="stars">
                <path d="M29.25,15c0.966,0,1.75-0.783,1.75-1.75V2.75C31,1.784,30.216,1,29.25,1h-10.5C17.784,1,17,1.784,17,2.75v10.5
      c0,0.967,0.784,1.75,1.75,1.75H29.25z M22.804,6L24,2.32L25.196,6h3.869l-3.13,2.451l1.195,3.77L24,9.99l-3.13,2.296l1.195-3.846
      L18.935,6H22.804z"></path>
      <path d="M45.25,15c0.967,0,1.75-0.783,1.75-1.75V2.75C47,1.784,46.217,1,45.25,1h-10.5C33.784,1,33,1.784,33,2.75v10.5
      c0,0.967,0.784,1.75,1.75,1.75H45.25z M38.804,6L40,2.32L41.195,6h3.869l-3.129,2.451l1.195,3.77L40,9.99l-3.13,2.296l1.195-3.846
      L34.935,6H38.804z"></path>
      <path d="M13.25,15c0.966,0,1.75-0.783,1.75-1.75V2.75C15,1.784,14.216,1,13.25,1H2.75C1.784,1,1,1.784,1,2.75v10.5
      C1,14.217,1.784,15,2.75,15H13.25z M6.804,6L8,2.32L9.196,6h3.869l-3.13,2.451l1.195,3.77L8,9.99l-3.13,2.296L6.065,8.44L2.935,6
      H6.804z"></path>
      <path d="M61.25,15c0.967,0,1.75-0.783,1.75-1.75V2.75C63,1.784,62.217,1,61.25,1h-10.5C49.783,1,49,1.784,49,2.75v10.5
      c0,0.967,0.783,1.75,1.75,1.75H61.25z M54.805,6L56,2.32L57.195,6h3.869l-3.129,2.451l1.195,3.77L56,9.99l-3.131,2.296l1.195-3.846
      L50.936,6H54.805z"></path>
      <path d="M77.25,1h-10.5C65.783,1,65,1.784,65,2.75v10.5c0,0.967,0.783,1.75,1.75,1.75h10.5c0.967,0,1.75-0.783,1.75-1.75V2.75
      C79,1.784,78.217,1,77.25,1z M75.131,12.221L72,9.99l-3.131,2.296l1.195-3.846L66.936,6h3.869L72,2.32L73.195,6h3.869l-3.129,2.451
      L75.131,12.221z"></path>
            </clipPath>
            </defs>
            <g id="bar" clip-path="url(#stars)">
              <rect fill="rgba(0, 0, 0, .1)" x="0" y="0" height="16px" width="80px"></rect>
              <rect class=${classMap({ 'rating-bar': true, 'preview-state': this.previewValue > 0 })} x="0" y="0" height="16px" width="${this._percentage(this.previewValue || this.value, 5)}"></rect>
            </g>
          </svg>
          <slot id="radios" name="label"></slot>
        </figure>
        <span class="rating-value" ?hidden="${!(this.value && this.displayValue)}"><strong>${this._formatNumber(this.value, this.decimalSeparatorLabel)}</strong> / ${this._formatNumber(5, this.decimalSeparatorLabel)}</span>
        <span class="rating-count" ?hidden="${!(this.countLabel && this.count)}">${pluralize(this.countLabel, this.count)}</span>
      </div>
    `;
  }

  static get properties() {
    return {
      /**
       * Text that will be displayed
       */
      text: {
        type: String,
      },

      /**
       * Rating value. Will set the visible stars
       * It should ve a value between 0.0 and 5.0
       */
      value: {
        type: Number,
      },

      /**
       * Value for the preview
       */
      previewValue: {
        type: Number,
        attribute: 'preview-value',
      },

      /**
       * Number of ratings taken into account
       */
      count: {
        type: Number,
      },

      /**
       * Value to be displayed
       */
      displayValue: {
        type: Boolean,
        attribute: 'display-value',
      },

      /**
       * Separator for the value.
       * Ex: ","
       */
      decimalSeparatorLabel: {
        type: String,
        attribute: 'decimal-separator-label',
      },

      /**
       * Label for the count
       */
      countLabel: {
        type: String,
        attribute: 'count-label',
      },
    };
  }

  constructor(props) {
    super(props);
    this.text = this.countLabel = '';
    this.decimalSeparatorLabel = ',';
    this.previewValue = this.value = this.count = 0;
    this.displayValue = false;
    this.distributedLabels = [];

    this._updateDistributedLabels = this._updateDistributedLabels.bind(this);
    this._clearPreviewValue = this._clearPreviewValue.bind(this);
    this._handleRadioMouseover = this._handleRadioMouseover.bind(this);
    this._handleRadioChange = this._handleRadioChange.bind(this);
  }

  firstUpdated() {
    this.$ = {};
    this.$.radios = this.shadowRoot.querySelector('#radios');

    if (this.$.radios) {
      this._updateDistributedLabels();
      this.$.radios.addEventListener('slotchange', this._updateDistributedLabels);
    }
  }

  _updateDistributedLabels() {
    const labels = this.$.radios.assignedNodes({ flatten: true }).filter((n) => n.nodeType === Node.ELEMENT_NODE);
    const selectedLabel = labels.filter((label) => label.querySelector(':checked'))[0];

    this.distributedLabels = labels;
    if (labels.length) {
      this._listenToRadioEvents();
    }

    if (selectedLabel) {
      this._clearPreviewValue();
      this.value = this.distributedLabels.indexOf(selectedLabel) + 1;
    }
  }

  _listenToRadioEvents() {
    const scale = this.shadowRoot.querySelector('#scale');
    scale.addEventListener('mouseleave', this._clearPreviewValue);
    scale.addEventListener('mouseover', this._handleRadioMouseover);
    scale.addEventListener('change', this._handleRadioChange);
  }

  _clearPreviewValue() {
    this.previewValue = 0;
  }

  _handleRadioMouseover(event) {
    this.previewValue = this.distributedLabels.indexOf(event.target) + 1;
  }

  _handleRadioChange(event) {
    const selectedLabel = this.distributedLabels.find((label) => label.contains(event.target));
    if (selectedLabel) {
      this._clearPreviewValue();
      this.value = this.distributedLabels.indexOf(selectedLabel) + 1;
    }
  }

  _percentage(fraction, total) {
    return `${Math.round((fraction / total) * 100)}%`;
  }

  _formatNumber(number, decimalSeparator) {
    return parseFloat(Math.round(number * 10) / 10).toFixed(1).replace('.', decimalSeparator);
  }
}

customElements.define(FitRating.is, FitRating);
